'use client';

import { useMemo, useState } from 'react';
import * as Lucide from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

const BLOCKED = new Set(['Icon', 'icons', 'createLucideIcon', 'LucideProvider', 'default', 'LucideIcon', 'IconNode']);

function allIconNames(): string[] {
  return Object.keys(Lucide)
    .filter((name) => /^[A-Z]/.test(name) && !BLOCKED.has(name))
    .sort();
}

export function IconView({ name, className }: { name?: string; className?: string }) {
  if (!name) {
    return null;
  }
  const Icon = (Lucide as unknown as Record<string, React.ComponentType<{ className?: string }>>)[name];
  return Icon ? <Icon className={className} /> : null;
}

export function IconPicker({
  value,
  onChange,
  className,
  names: namesOverride,
}: {
  value?: string;
  onChange: (icon: string | undefined) => void;
  className?: string;
  /** Optional allowlist (e.g. TECH_ICON_NAMES) — defaults to every lucide icon. */
  names?: string[];
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const names = useMemo(() => (namesOverride ? [...namesOverride].sort() : allIconNames()), [namesOverride]);

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    const list = term ? names.filter((name) => name.toLowerCase().includes(term)) : names;
    return list.slice(0, 120);
  }, [names, query]);

  return (
    <div className={cn('relative', className)}>
      <Button
        type="button"
        variant="outline"
        onClick={() => setOpen((prev) => !prev)}
        className="w-full justify-start gap-2"
      >
        <IconView name={value} className="h-4 w-4" />
        <span className="truncate text-xs">{value ?? 'بدون أيقونة'}</span>
      </Button>

      {open && (
        <div className="absolute z-50 mt-2 w-72 rounded-xl border border-border bg-popover p-3 shadow-lg">
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="ابحث عن أيقونة..."
            className="mb-2"
            autoFocus
          />
          <div className="grid max-h-56 grid-cols-6 gap-1 overflow-y-auto">
            {filtered.map((name) => (
              <button
                key={name}
                type="button"
                title={name}
                onClick={() => {
                  onChange(name);
                  setOpen(false);
                }}
                className={cn(
                  'flex h-9 w-9 items-center justify-center rounded-lg hover:bg-muted',
                  value === name && 'bg-primary/15 text-primary'
                )}
              >
                <IconView name={name} className="h-4 w-4" />
              </button>
            ))}
          </div>
          <div className="mt-2 flex justify-between">
            <Button type="button" variant="ghost" size="sm" onClick={() => { onChange(undefined); setOpen(false); }}>
              إزالة
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>
              إغلاق
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
